import { c as _c } from "react/compiler-runtime";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useConnectedWalletCapability } from '../connected-wallet/connected-wallet-react.js';
import { fundingContextQuery } from '../discovery/context-query.js';
import { withdrawalContextQuery } from '../offramp/context-query.js';
import { bindOnrampCache } from '../operations/cache-lifecycle.js';
import { useFundingFunnel } from '../operations/funnel-react.js';
import { fundingSessionQuery } from './session-query.js';
/** Portfolio mounts inactive discovery; payment and connection frames wait for entry. */
export function useFundingEntryWarmup(client, options) {
  const $ = _c(38);
  const cache = useQueryClient();
  const {
    active,
    enabled,
    mode,
    currency,
    surface
  } = options;
  const [entered, setEntered] = useState(active);
  const opened = useRef(false);
  useConnectedWalletCapability(client, surface, (enabled || active) && mode === "deposit");
  let t0;
  if ($[0] !== cache || $[1] !== client) {
    t0 = fundingSessionQuery(client, cache);
    $[0] = cache;
    $[1] = client;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const t1 = (enabled || active) && mode !== "withdraw";
  let t2;
  if ($[3] !== t0 || $[4] !== t1) {
    t2 = {
      ...t0,
      enabled: t1
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const session = useQuery(t2);
  let t3;
  if ($[6] !== client || $[7] !== currency || $[8] !== surface) {
    t3 = withdrawalContextQuery(client, currency, surface);
    $[6] = client;
    $[7] = currency;
    $[8] = surface;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const t4 = (enabled || active) && mode === "withdraw";
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = {
      ...t3,
      enabled: t4,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false
    };
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const withdrawal = useQuery(t5);
  const error = mode === "withdraw" ? withdrawal.error : session.error;
  const retainDepositProvider = mode === "deposit" && session.isSuccess;
  const t6 = error ? "error" : "loading";
  const t7 = error ? "provider_unavailable" : null;
  let t8;
  if ($[13] !== t6 || $[14] !== t7) {
    t8 = {
      stage: "session",
      outcome: t6,
      errorCode: t7
    };
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  useFundingFunnel(client, t8, Boolean(error));
  let t10;
  let t9;
  if ($[16] !== cache || $[17] !== client) {
    t9 = () => {
      bindOnrampCache(client, cache);
    };
    t10 = [client, cache];
    $[16] = cache;
    $[17] = client;
    $[18] = t10;
    $[19] = t9;
  } else {
    t10 = $[18];
    t9 = $[19];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[20] !== active || $[21] !== cache || $[22] !== client || $[23] !== currency || $[24] !== enabled || $[25] !== entered || $[26] !== retainDepositProvider || $[27] !== surface) {
    t11 = () => {
      if (enabled || active || entered || retainDepositProvider) {
        return;
      }
      cache.cancelQueries({
        queryKey: ["onramp", client.cacheKey, "session", 0],
        exact: true
      });
      cache.cancelQueries({
        queryKey: ["onramp", client.cacheKey, "context"],
        exact: true
      });
      cache.cancelQueries({
        queryKey: ["onramp", client.cacheKey, "withdrawal-context", currency, surface],
        exact: true
      });
    };
    t12 = [active, enabled, entered, retainDepositProvider, cache, client, currency, surface];
    $[20] = active;
    $[21] = cache;
    $[22] = client;
    $[23] = currency;
    $[24] = enabled;
    $[25] = entered;
    $[26] = retainDepositProvider;
    $[27] = surface;
    $[28] = t11;
    $[29] = t12;
  } else {
    t11 = $[28];
    t12 = $[29];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[30] !== active || $[31] !== cache || $[32] !== client || $[33] !== currency || $[34] !== mode || $[35] !== surface) {
    t13 = () => {
      if (!active) {
        return;
      }
      setEntered(true);
      const firstOpening = !opened.current;
      opened.current = true;
      let closed = false;
      const revalidate = async function revalidate() {
        if (mode === "withdraw") {
          await cache.invalidateQueries({
            queryKey: ["onramp", client.cacheKey, "withdrawal-context", currency, surface],
            exact: true
          });
          return;
        }
        const context = cache.getQueryState(["onramp", client.cacheKey, "context"]);
        const session_0 = cache.getQueryState(["onramp", client.cacheKey, "session", 0]);
        const discoveryFailed = cache.getQueryCache().findAll({
          queryKey: ["onramp", client.cacheKey, "discovery"]
        }).some(_temp);
        if (firstOpening && context?.status === "success" && Date.now() - context.dataUpdatedAt < 60000 && session_0?.status !== "error" && !discoveryFailed) {
          return;
        }
        await cache.invalidateQueries({
          queryKey: ["onramp", client.cacheKey, "context"],
          exact: true,
          refetchType: "none"
        });
        await cache.fetchQuery({
          ...fundingContextQuery(client),
          staleTime: 0
        });
        if (closed) {
          return;
        }
        if (cache.getQueryState(["onramp", client.cacheKey, "session", 0])?.status === "error") {
          await cache.invalidateQueries({
            queryKey: ["onramp", client.cacheKey, "session", 0],
            exact: true
          });
        }
        await cache.invalidateQueries({
          queryKey: ["onramp", client.cacheKey, "discovery"]
        });
      };
      revalidate().catch(() => {
        if (!closed) {
          client.observeFunnel?.({
            stage: "session",
            outcome: "error",
            errorCode: "onramp_refresh_failed"
          });
        }
      });
      return () => {
        closed = true;
      };
    };
    t14 = [active, cache, client, mode, currency, surface];
    $[30] = active;
    $[31] = cache;
    $[32] = client;
    $[33] = currency;
    $[34] = mode;
    $[35] = surface;
    $[36] = t13;
    $[37] = t14;
  } else {
    t13 = $[36];
    t14 = $[37];
  }
  useEffect(t13, t14);
  return active || entered || retainDepositProvider || enabled && mode === "deposit";
}
function _temp(t0) {
  const {
    state
  } = t0;
  return state.status === "error";
}